import CommercialInvoice from "@dana/Components/OrderHistory/CommercialInvoice";
import Invoice from "@dana/Components/OrderHistory/Invoice";
import PackingSlip from "@dana/Components/OrderHistory/PackingSlip";
import ProformaInvoice from "@dana/Components/OrderHistory/ProformaInvoice";
import VerticalTabs, { VerticalTab } from "@dana/Components/OrderHistory/VerticleTabs";
import InvoiceIcon from "@dana/Icons/InvoiceIcon";;
import Truck from "@dana/MobiusOverrides/Icons/Truck";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import GetDeliveryNumbersHandler from "@dana/Store/Data/OrderHistory/Handlers/GetDanaDeliveryNumbersHandler";
import GetDanaInvoiceData from "@dana/Store/Data/OrderHistory/Handlers/GetDanaInvoiceHandler";
import GetDanaPackingSlipNumberData from "@dana/Store/Data/OrderHistory/Handlers/GetDanaPackingSlipHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ShippingTracking from "@dana/Components/OrderHistory/ShippingTracking";
import GetShippingTrackingData from "@dana/Store/Data/OrderHistory/Handlers/GetShippingTrackingHandler";
import { NARegion } from "@dana/Common/Constants";

type InvoiceModalType = "Tracking" |"PackingSlip" | "Invoice" | "Commercial" | "Proforma" | null;

const mapStateToProps = (state: ApplicationStateDana) => ({
    danaPackingSlipData: state.danaPackingSlip,
    danaInvoiceData: state.danaInvoice,
    danaDeliveryNumbersData:state.danaDeliveryNumbers,
    danaShipingTrackingData: state.danaShippingTracking,
    danawebsite: state.context.website,
    selectedRegion: state.context.session.language?.properties?.region,
});

const mapDispatchToProps = {
    GetDanaPackingSlipNumberData,
    GetDanaInvoiceData,
    GetDeliveryNumbersHandler,
    GetShippingTrackingData,
};

interface InvoiceButtonsAndModalsProps {
    orderNumber?: string;
    packingSlipNumber?: string;
    invoiceNumber?: string;
    commercialNumber?: string;
    proformatInvoiceNumber?: string;
    showPackingSlip?: boolean;
    showShippingTracking?: boolean;
    showInvoice?: boolean;
    showCommercialInvoice?: boolean;
    showProformaInvoice?: boolean;
}

type OwnProps = InvoiceButtonsAndModalsProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export interface InvoiceButtonsStyles {
    invoicesButtons?: InjectableCss;
    invoiceButton?: ButtonPresentationProps;
    trackButton?:ButtonPresentationProps;
    modal?: ModalPresentationProps;
}

export const invoiceButtonsStyles: InvoiceButtonsStyles = {
    invoicesButtons: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 8px 0;
        `,
    },
    invoiceButton: {
        icon: {
            src: InvoiceIcon,
            position: "left",
        },
        variant: "secondary",
        typographyProps: { size: 14 },
        css: css`
            padding: 8px;
        `,
    },
    trackButton: {
        icon: {
            src: Truck,
            position: "left",
        },
        variant: "secondary",
        typographyProps: { size: 14 },
        css: css`
            padding: 8px;
        `,
    },
    modal: { sizeVariant: "large", size: 1200 },
};

const styles = invoiceButtonsStyles;

const normalizeNumbersList = (arr: Array<string | number> = []): string[] => {
    const splitRegex = /[,\s|;]+/;
    const out: string[] = [];

    for (const item of arr) {
        if (item == null) {
            continue;
        }
        const s = String(item).trim();
        if (!s) {
            continue;
        }

        // If backend sent a JSON array as string, e.g. '["1001","1002"]' or '[1001,1002]'
        if (s.startsWith("[") && s.endsWith("]")) {
            try {
                const parsed = JSON.parse(s);
                if (Array.isArray(parsed)) {
                    for (const v of parsed) {
                        const str = String(v).trim();
                        if (str) {
                            out.push(str);
                        }
                    }
                    continue; // handled this item fully
                }
            } catch {
                // ignore JSON parse errors and fall through to normal split
            }
        }

        // Normal case: "1002440937,1002440949"
        const parts = s.split(splitRegex);
        for (const p of parts) {
            const t = p.trim();
            if (t) {
                out.push(t);
            }
        }
    }

    // Deduplicate while preserving order
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const v of out) {
        if (!seen.has(v)) {
            seen.add(v);
            unique.push(v);
        }
    }
    return unique;
};

const InvoiceButtonsAndModals = ({
    orderNumber,
    packingSlipNumber,
    invoiceNumber,
    commercialNumber,
    proformatInvoiceNumber,
    showShippingTracking=  orderNumber !== undefined && orderNumber !== null && orderNumber !== "",
    showPackingSlip = packingSlipNumber !== undefined && packingSlipNumber !== null && packingSlipNumber !== "",
    showInvoice = invoiceNumber !== undefined && invoiceNumber !== null && invoiceNumber !== "",
    showCommercialInvoice = commercialNumber !== undefined && commercialNumber !== null && commercialNumber !== "",
    showProformaInvoice = proformatInvoiceNumber !== undefined &&
        proformatInvoiceNumber !== null &&
        proformatInvoiceNumber !== "",
    GetDanaPackingSlipNumberData,
    GetDanaInvoiceData,
    GetDeliveryNumbersHandler,
    GetShippingTrackingData,
    danaDeliveryNumbersData,
    danaPackingSlipData,
    danaInvoiceData,
    danawebsite,
    selectedRegion,
    danaShipingTrackingData,
}: OwnProps) => {
    const [openModal, setOpenModal] = useState<InvoiceModalType>(null);
    const [deliveryNumber, setDeliveryNumber] = useState<string>();

    // List of numbers shown in the left tab list for the current open modal type
    const [numbersList, setNumbersList] = useState<string[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [isFetching, setIsFetching] = useState(false);

    /** -------- Helpers to wire API and state ---------- */

    // Extract the list of numbers from API response for the current type
    const extractNumbersFromResponse = (type: Exclude<InvoiceModalType, null>): string[] => {
        // ⚠️ Adjust this to match your real API response shape
        // Common patterns supported here:
        // - res.list / res.numbers
        // - res.items?.map(x => x.number)
        // - res.relatedNumbers
        const source = type === "PackingSlip" ? danaPackingSlipData?.data : (type === "Tracking"?danaShipingTrackingData?.data:danaInvoiceData?.data);

        if (!source) {
            return [];
        }

        const tryArrays: Array<unknown> = [
            (source as any).numbers,
            (source as any).list,
            (source as any).relatedNumbers,
            (source as any).items,
            (source as any).invoices,
        ].filter(Boolean);

        for (const candidate of tryArrays) {
            if (Array.isArray(candidate)) {
                const maybeNumbers = candidate
                    .map((x: any) => (typeof x === "string" ? x : x?.number ?? x?.id))
                    .filter(Boolean);
                if (maybeNumbers.length > 0) {
                    return Array.from(new Set(maybeNumbers));
                }
            }
        }

        // If response itself is an array of objects or strings
        if (Array.isArray(source)) {
            const maybeNumbers = source
                .map((x: any) => (typeof x === "string" ? x : x?.number ?? x?.id))
                .filter(Boolean);
            if (maybeNumbers.length > 0) {
                return Array.from(new Set(maybeNumbers));
            }
        }

        // No list found in API; fall back to the initial provided number
        switch (type) {
            case "Tracking":
                return orderNumber ? [orderNumber] : [];
            case "PackingSlip":
                return deliveryNumber ? [deliveryNumber] : [];
            case "Invoice":
                return invoiceNumber ? [invoiceNumber] : [];
            case "Commercial":
                return commercialNumber ? [commercialNumber] : [];
            case "Proforma":
                return proformatInvoiceNumber ? [proformatInvoiceNumber] : [];
            default:
                return [];
        }
    };

    const fetchDetailForNumber = async (type: Exclude<InvoiceModalType, null>, number: string) => {
        setIsFetching(true);
        try {
            if (type === "PackingSlip") {
                await GetDanaPackingSlipNumberData({ requestNumber: number });
            } else if (type === "Tracking"){
                await GetShippingTrackingData({orderNumber:number});
            }else
                {
                await GetDanaInvoiceData({ requestNumber: number, requestType: type });
            }
        } finally {
            setIsFetching(false);
        }
    };

    const getInitialNumberForType = (type: Exclude<InvoiceModalType, null>): string | null => {
        switch (type) {
             case "Tracking":
                return orderNumber ?? null;
            case "PackingSlip":
                return deliveryNumber ?? null;
            case "Invoice":
                return invoiceNumber ?? null;
            case "Commercial":
                return commercialNumber ?? null;
            case "Proforma":
                return proformatInvoiceNumber ?? null;
            default:
                return null;
        }
    };

    /** -------- Modal lifecycle ---------- */
    useEffect(() => {
        if (packingSlipNumber === orderNumber) {
            getDeliveryData();
        }
    },[openModal]);

    // When a modal opens, fetch detail for the initial number (if any)
    useEffect(() => {
        if (!openModal) {
            setNumbersList([]);
            setActiveTabId(null);
            setIsFetching(false);
            return;
        }

        const initialNumber = getInitialNumberForType(openModal);
        const normalizedInitial = initialNumber ? normalizeNumbersList([initialNumber])[0] : null;

        if (normalizedInitial) {
            setActiveTabId(normalizedInitial);
            fetchDetailForNumber(openModal, normalizedInitial);
        } else {
            setActiveTabId(null);
        }
    }, [openModal, orderNumber]);

    // As soon as the slice gets updated for the active type, try to populate/refresh the list

    useEffect(() => {
        if (!openModal) {
            return;
        }

        const rawNumbers = extractNumbersFromResponse(openModal);
        const normalizedNumbers = normalizeNumbersList(rawNumbers);

        if (normalizedNumbers.length > 0) {
            setNumbersList(normalizedNumbers);
            setActiveTabId(prev => (prev && normalizedNumbers.includes(prev) ? prev : normalizedNumbers[0]));
        } else {
            setNumbersList([]);
            setActiveTabId(null);
        }        
    }, [openModal, danaPackingSlipData?.data, danaInvoiceData?.data, danaShipingTrackingData?.data]);

    /** -------- UI helpers ---------- */

    const getDeliveryData = async () => {
         let response;
         GetDeliveryNumbersHandler({
                    orderNumber: packingSlipNumber,
                    onSuccess: () => {                       
                    },
                    onComplete: resultProps => {
                        response = resultProps.apiResult || undefined;        
                        if (response !== undefined) {                          
                            if (response?.deliveryNumbers) {
                                setDeliveryNumber(response?.deliveryNumbers);
                            } 
                        }
                    },
                    onError: (errorResponse: any) => {
                        // eslint-disable-next-line no-console
                    },
                });               
       
    };

    const openInvoiceModal = (type: InvoiceModalType) => () => setOpenModal(type);
    const handleCloseModal = () => setOpenModal(null);

    const getModalHeadline = () => {
        switch (openModal) {
            case "Tracking":
                return translate(`Order Number #${orderNumber}`);
            case "PackingSlip":
                return translate(`Packing Slips for Order Number ${orderNumber}`);
            case "Invoice":
                return translate(`Invoice for Order Number ${orderNumber}`);
            case "Commercial":
                return translate(`Commercial Invoice for Order Number ${orderNumber}`);
            case "Proforma":
                return translate(`Proforma Invoice for Order Number ${orderNumber}`);
            default:
                return "";
        }
    };

    const renderActiveTypeComponent = () => {
        if (!openModal) {
            return null;
        }

        // Use current slice data for the selected type
        if (openModal === "PackingSlip") {
            const data = danaPackingSlipData?.data;
            if (!data) {
                return null;
            }
            return <PackingSlip websiteData={danawebsite} invoiceData={data} selectedRegion={selectedRegion} />;
        }        

        // Invoice/Proforma/Commercial all come through danaInvoiceData
        const invData = danaInvoiceData?.data;
        if (!invData) {
            return null;
        }

        switch (openModal) {           
            case "Invoice":
                return <Invoice invoiceData={invData} selectedRegion={selectedRegion} websiteData={danawebsite} />;
            case "Commercial":
                return <CommercialInvoice selectedRegion={selectedRegion} cominvoiceData={invData} websiteData={danawebsite} />;
            case "Proforma":
                return <ProformaInvoice selectedRegion={selectedRegion} proformainvoiceData={invData} websiteData={danawebsite} />;
            default:
                return null;
        }
    };

    const onTabChange = async (tabId: string) => {
        if (!openModal) {
            return;
        }
        setActiveTabId(tabId);
        await fetchDetailForNumber(openModal, tabId);
    };

    const getModalContent = () => {
        if (!openModal) {
            return null;
        }

        // Empty-state if nothing to show
        if (numbersList.length === 0 && !isFetching) {
            return <div style={{ padding: 16 }}>{translate("Loading document...")}</div>;
        }
        
        if (numbersList.length === 0) {
           return <div style={{ padding: 16 }}>{translate("No documents found for this order.")}</div>;
        }

        // Build Vertical Tabs from numbers list
        const tabs: VerticalTab[] = numbersList.map(num => ({
            id: num,
            label: num,
            content: () => (
                <>
                    {isFetching && (
                        <div style={{ marginBottom: 12, color: "#6b7280", fontSize: 12 }}>
                            {translate("Loading document...")}
                        </div>
                    )}
                    {renderActiveTypeComponent()}
                </>
            ),
        }));

        const leftTitle = openModal === "PackingSlip" ? translate("Delivery Numbers") : translate("Invoice Numbers");

        return (
            <>  
            {openModal === "Tracking" ? (
            <ShippingTracking trackingData={danaShipingTrackingData?.data} /> 
            ):(       
            <VerticalTabs
                tabs={tabs}
                initialTabId={activeTabId ?? tabs[0]?.id}
                height="auto"
                leftWidth="195px" // optional
                leftTitle={leftTitle}
                onChange={onTabChange}
            />
            )}
            </>
        );
    };

    return (
        <StyledWrapper {...styles.invoicesButtons}>
             {(showShippingTracking && selectedRegion===NARegion) && (
                <Button onClick={openInvoiceModal("Tracking")} {...styles.trackButton}>
                    {translate("Tracking")}
                </Button>
            )}
            {showPackingSlip && (
                <Button onClick={openInvoiceModal("PackingSlip")} {...styles.invoiceButton}>
                    {translate("Packing Slip")}
                </Button>
            )}
            {showInvoice && (
                <Button onClick={openInvoiceModal("Invoice")} {...styles.invoiceButton}>
                    {translate("Invoice")}
                </Button>
            )}
            {showCommercialInvoice && (
                <Button onClick={openInvoiceModal("Commercial")} {...styles.invoiceButton}>
                    {translate("Commercial Invoice")}
                </Button>
            )}
            {showProformaInvoice && (
                <Button onClick={openInvoiceModal("Proforma")} {...styles.invoiceButton}>
                    {translate("Proforma Invoice")}
                </Button>
            )}

            <Modal {...styles.modal} headline={getModalHeadline()} isOpen={!!openModal} handleClose={handleCloseModal}>
                {getModalContent()}
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceButtonsAndModals);
